<template>
  <component
    :is="url ? 'a' : 'div'"
    :href="url ? url : undefined"
    class="gmf-quest"
    :class="{ 'gmf-quest-completed': quest.achievedAt, 'gmf-clickable': !!url }"
    @click="handleClick"
  >
    <div class="gmf-quest-content">
      <div class="gmf-quest-image-col">
        <img class="gmf-quest-image" :src="quest.imageUrl" />
        <div v-if="quest.achievedAt" class="gmf-quest-completion-icon">
          <Icon icon="checkmark" />
        </div>
      </div>
      <div class="gmf-quest-body">
        <div class="gmf-quest-name gmf-subheader">{{ quest.name }}</div>
        <div v-if="quest.description" class="gmf-quest-description">{{ quest.description }}</div>
        <table class="gmf-quest-goal-list">
          <div class="gmf-quest-goal" v-for="(goal, i) in quest.goals" :key="i">
            <div class="gmf-quest-goal-label">{{ goal.label }}</div>
            <div class="gmf-quest-goal-progress" v-if="!quest.achievedAt">
              <ProgressBar :min="0" :max="goal.targetProgressValue" :value="goal.progress" />
              <div class="gmf-quest-goal-progress-number">{{ goal.progress }} / {{ goal.targetProgressValue }}</div>
            </div>
          </div>
        </table>
      </div>
    </div>
    <div class="gmf-quest-foot">
      <template v-if="showRewards">
        <VerticalSeparator>
          {{ t('quests.rewards') }}
        </VerticalSeparator>
        <div class="gmf-quest-reward-list">
          <div class="gmf-quest-reward" v-if="xp">
            <Icon v-if="showIcons" icon="point_star" class="gmf-quest-token-icon" />
            <span class="gmf-quest-reward-quantity">{{ xp.quantity }}</span>
            <span class="gmf-quest-reward-name">{{ xp.name }}</span>
          </div>
          <div class="gmf-quest-reward" v-if="bp">
            <span class="gmf-quest-reward-quantity">{{ bp.quantity }}</span>
            <span class="gmf-quest-reward-name">{{ bp.name }}</span>
          </div>
          <div class="gmf-quest-reward" v-for="part in specificAvatarPart" :key="part.name">
            <img :src="part.url" class="gmf-quest-token-icon" />
            <span class="gmf-quest-reward-name">{{ part.name }}</span>
          </div>
          <div class="gmf-quest-reward" v-if="remainingAvatarParts?.quantity > 0">
            <Icon v-if="showIcons" icon="avatar_clothing" class="gmf-quest-token-icon" />
            <span class="gmf-quest-reward-quantity">{{ remainingAvatarParts.quantity }}</span>
            <span class="gmf-quest-reward-name">{{ remainingAvatarParts.name }}</span>
          </div>
          <div class="gmf-quest-reward" v-if="tokens">
            <img v-if="showIcons" :src="tokens.icon" class="gmf-quest-token-icon" />
            <span class="gmf-quest-reward-quantity">{{ tokens.quantity }}</span>
            <span class="gmf-quest-reward-name">{{ tokens.name }}</span>
          </div>
          <div class="gmf-quest-reward" v-if="lootcrate">
            <img v-if="lootcrate.icon" :src="lootcrate.icon" class="gmf-quest-token-icon" />
            <span v-if="lootcrate.quantity > 1" class="gmf-quest-reward-quantity">{{ lootcrate.quantity }}</span>
            <span class="gmf-quest-reward-name">{{ lootcrate.name }}</span>
          </div>
        </div>
      </template>
      <div class="gmf-quest-status-container">
        <Icon icon="link" class="gmf-quest-link-icon" v-if="url" />
        <div class="gmf-link gmf-text-small gmf-quest-link" v-if="url">
          {{ t('quests.link') }}
        </div>
        <div class="gmf-quest-status gmf-text-small" v-if="quest.achievedAt">{{ t('quest_completed') }}</div>
        <div class="gmf-quest-status gmf-text-small" v-else-if="quest.achievableUntil">
          <Timer :time="durationSeconds" :passedTime="durationPassed" />
        </div>
      </div>
    </div>
  </component>
</template>

<style>
.gmf-quest {
  padding: var(--gmf-p-4);
  background: var(--gmf-quest-bg);
  border-radius: var(--gmf-rounding);
  display: block;
}

.gmf-quest .gmf-progress-bar {
  background: var(--gmf-col-white);
}

.gmf-quest-content {
  display: flex;
}

.gmf-quest-description {
  margin-bottom: var(--gmf-p-3);
}

.gmf-quest-body {
  padding-left: var(--gmf-p-4);
  flex-grow: 1;
}

.gmf-quest-foot {
  padding-top: var(--gmf-p-4);
}

.gmf-quest-reward-list {
  display: flex;
  gap: var(--gmf-p-4);
  justify-content: center;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-top: var(--gmf-p-4);
  margin-bottom: var(--gmf-p-3);
}

.gmf-context .gmf-quest-reward-quantity {
  font-weight: bold;
  font-size: var(--gmf-subheader-font-size);
  color: var(--gmf-subheader-color);
}

.gmf-context .gmf-quest-status-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.gmf-quest-image-col {
  position: relative;
  flex-shrink: 0;
}

.gmf-quest-image {
  width: 64px;
  height: 64px;
}

.gmf-quest-image-col .gmf-quest-completion-icon {
  position: absolute;
  inset: -8px;
  width: 24px;
  height: 24px;
  background: var(--gmf-brand-color);
  border-radius: 100%;
  --gmf-icon-color: var(--gmf-col-white);
  display: flex;
  justify-content: center;
  align-items: center;
}

.gmf-quest-completed .gmf-quest-image {
  filter: grayscale(0.5) opacity(0.5);
}

.gmf-quest-goal-list {
  width: 100%;
}

.gmf-quest-goal-label {
  margin-bottom: var(--gmf-p-2);
}

.gmf-quest-goal-progress {
  margin-top: var(--gmf-p-3);
  display: flex;
  align-items: center;
}

.gmf-context .gmf-quest-goal-progress-number {
  white-space: nowrap;
  padding-left: var(--gmf-p-3);
}

.gmf-quest-goal:not(:last-child) {
  margin-bottom: var(--gmf-p-3);
}

.gmf-quest-reward {
  display: flex;
  align-items: center;
  gap: var(--gmf-p-2);
}

.gmf-quest-token-icon {
  width: 24px;
  height: 24px;
}

.gmf-quest-link {
  flex-grow: 1;
}

.gmf-quest-link-icon {
  margin-right: var(--gmf-p-1);
  --gmf-icon-color: var(--gmf-link-color);
  height: 14px;
  width: 14px;
}

.gmf-quest.gmf-clickable:hover .gmf-quest-link {
  text-decoration: underline;
}
</style>

<script>
import ProgressBar from '@/components/ProgressBar';
import Timer from '@/components/Timer';
import state from '@/state';
import Icon from '@/components/Icon';
import VerticalSeparator from '@/components/VerticalSeparator';
import eventbus from '@/utils/eventbus';
import hooks from '@/utils/hooks';

export default {
  props: {
    quest: {
      Type: Object,
      required: true,
    },
    showRewards: {
      Type: Boolean,
      default: true,
    }
  },
  components: {
    ProgressBar,
    Timer,
    Icon,
    VerticalSeparator,
  },
  methods: {
    t: state.t,
    getAvatarPartPhrase(quantity) {
      if (quantity === 1) {
        return this.t('loot_crates.avatar_part');
      }
      return this.t('loot_crates.avatar_part_plural');
    },
    handleClick(evt) {
      if (!this.url) {
        return;
      }

      evt.preventDefault();
      evt.stopPropagation();

      // @TODO duplicated from Link.vue, can we reuse that component?
      let handled = false;

      const data = {
        url: this.url,
      };

      // This is for SPA navigation. We probably never want to keep a modal open aftre navigation
      eventbus.emit('modal.close');

      if (!evt.ctrlKey) {
        handled = hooks.run('navigation', data);
      }
      if (!handled) {
        if (evt.ctrlKey) {
          window.open(data.url, '_blank');
        } else {
          window.location = data.url;
        }
      }
    },
  },
  computed: {
    url() {
      if (this.quest.achievedAt) {
        return null;
      }
      return this.quest.url;
    },
    showIcons() {
      return state.merchantConfig.quests?.showRewardIcons || false;
    },
    tokens() {
      const r = this.quest.newRewards || this.quest.rewards;
      return r.tokens || null;
    },
    lootcrate() {
      const r = this.quest.newRewards || this.quest.rewards;
      return r['loot-crate'] || null;
    },
    xp() {
      const r = this.quest.newRewards || this.quest.rewards;

      const xp = r.xp || r.ip;

      if (typeof xp === 'object') {
        return {
          quantity: xp.quantity,
          name: this.t('xp'),
        }
      }

      // Xp is a number (legacy format for backwards compatibility)
      if (typeof xp === 'number') {
        return {
          quantity: xp,
          name: this.t('xp'),
        };
      }

      return null;
    },
    specificAvatarPart() {
      const rewards = this.quest.newNewRewards;
      let specificAvatarParts = [];
      for (const reward of rewards) {
        if (reward.type === 'avatar-3d-part' && reward.partUuid) {
          specificAvatarParts.push({
            quantity: parseInt(reward.quantity) || 0,
            name: reward.name || this.getAvatarPartPhrase(reward.quantity),
            url: state.cdnBase + '/v2/avatar-part/' + reward.partUuid,
          });
        }
      }

      return specificAvatarParts;
    },
    remainingAvatarParts() {
      const rewards = this.quest.newNewRewards;

      let quantity = 0;
      for (const reward of rewards) {
        if (reward.type === 'avatar-3d-part') {
          quantity += parseInt(reward.quantity);
        }
      }

      for (const part of this.specificAvatarPart) {
        quantity -= part.quantity;
      }

      return {
        quantity,
        name: this.getAvatarPartPhrase(quantity),
      }
    },
    bp() { // bonus points
      const r = this.quest.newRewards || this.quest.rewards;

      const bp = r.bp;

      if (typeof bp === 'object') {
        return {
          quantity: bp.quantity,
          name: this.t('rewards.bp.label'),
        }
      }

      return null;
    },
    durationSeconds() {
      const start = new Date(this.quest.achievableFrom);
      const end = new Date(this.quest.achievableUntil);
      return Math.floor((end - start) / 1000);
    },
    durationPassed() {
      const start = new Date(this.quest.achievableFrom);
      const now = new Date();
      return Math.floor((now - start) / 1000);
    },
  },
};

</script>
